import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { ArrowLeft, FileText } from "lucide-react";

/** Renders a policy doc; uses very simple Markdown-to-HTML (## → h2). */
function renderContent(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const blocks = [];
  let buf = [];
  const flush = () => {
    if (buf.length) {
      blocks.push(<p key={blocks.length} className="text-zinc-700 leading-relaxed mb-4 whitespace-pre-line">{buf.join("\n")}</p>);
      buf = [];
    }
  };
  lines.forEach((ln) => {
    if (ln.startsWith("## ")) {
      flush();
      blocks.push(
        <h2 key={blocks.length} className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-8 mb-3">
          {ln.replace("## ", "")}
        </h2>
      );
    } else if (ln.startsWith("- ")) {
      buf.push(`• ${ln.slice(2)}`);
    } else if (ln.trim() === "") {
      flush();
    } else {
      // Bold **text**
      buf.push(ln.replace(/\*\*(.+?)\*\*/g, "$1"));
    }
  });
  flush();
  return blocks;
}

export default function Policy() {
  const { slug } = useParams();
  const { t, isRTL } = useI18n();
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setPolicy(null);
    api.get(`/policies/${slug}`).then((r) => setPolicy(r.data)).catch((e) => setError(e.response?.data?.detail || "Not found"));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="surface-card p-10 text-center max-w-md">
          <h1 className="font-display text-3xl font-bold">404</h1>
          <p className="text-zinc-500 mt-2">{error}</p>
          <Link to="/" className="btn btn-secondary mt-6 inline-flex">
            <ArrowLeft className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} strokeWidth={2} />
            Home
          </Link>
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="surface-card px-5 py-3 text-sm text-zinc-500">{t("common_loading")}</div>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
      <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono mb-3" data-testid="policy-breadcrumb">
        <FileText className="w-3.5 h-3.5" strokeWidth={2} />
        <span>{slug.toUpperCase()}</span>
        <span>·</span>
        <span>{policy.updated_at?.split("T")[0]}</span>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-8" data-testid="policy-title">
        {policy.title}
      </h1>
      <div className="prose prose-zinc max-w-none" data-testid="policy-content" dir="ltr">
        {renderContent(policy.content)}
      </div>
    </article>
  );
}
